import { Injectable, Injector } from '@angular/core';
import { BaseApiService } from '@carm/_core/baseapi.service';
import { IBaseResponse } from '@carm/_utils/models';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  IBusinessProfileResponse,
  IBusinessSettingsAndPreferencesResponse,
  IProgramListResponse,
  IProgramProfileResponse,
  IProgramAddressResponse,
  IBusinessContactResponse,
  IBusinessOwnershipResponse,
} from '../models';
import {
  IBusinessSettingsAndPreferences,
  IProgramInformation,
  IBusinessProfileInformationContact,
} from '../models/program-account-profile.model';
import {
  IBaseContactResponse,
  IBaseContactsResponse,
  IProgramEdiServiceListResponse,
  IProgramEnrolmentStatusResponse,
} from '@carm/_core/program-enrolment/models/enrol-program-api.model';
import {
  INativeProgramListResponse,
  ISubProgramActiveResponse,
  IBusinessProfileContactInfoResponse,
  IBusinessContactsResponse,
  ICbsaProgramListResponse,
  IInactiveProgramResponse,
} from '../models/program-account-profile-api.model';
import {
  IBaseAddressListResponse,
  IBaseAddressResponse,
} from '@carm/_core/access-control-mgt/models/address-api.model';
import {
  IBaseAddress,
  IBaseSocialMedia,
} from '@carm/shared/components/form-elements/models';
import { IBaseAddressSubmitModel } from '@carm/_core/business-registration/models';
import { ProgramEnrolmentType } from '@carm/_core/program-enrolment/models/enrol-program-enums.model';
import { environment } from 'src/environments/environment';
import { mapToProgramInformationSubmitRequest, mapToProfileInformationContactToSubmit, mapToBaseContactSubmit, mapContactToSubmitRequest, mapToBusinessSettingsAndPreferencesSubmit } from '@carm/_core/business-registration/converters';
import { mapBaseAddressToSubmitRequest, mapBaseSocialMediaToSubmitRequest } from '@carm/_core/program-enrolment/converters';
import { IContact } from '@carm/_core/business/models';
import { LoaderSpinnerTypes } from '@carm/_core';

@Injectable()
export class ProgramAccountProfileService extends BaseApiService {
  constructor(protected injector: Injector) {
    super(injector);
  }

  public getProgramList(loaderSpinnerType = LoaderSpinnerTypes.FullPage): Observable<IProgramListResponse> {
    const endPoint = `${this.endPointB}/programs/program-account-list`;
    return this.httpAuthGet<IProgramListResponse>(endPoint, null, loaderSpinnerType);
  }

  public getCbsaProgramList(): Observable<ICbsaProgramListResponse> {
    const endPoint = `${this.endPointB}/programs/cbsa-program-list`;
    return this.httpAuthGet<ICbsaProgramListResponse>(endPoint);
  }

  public getNativeProgramList(): Observable<INativeProgramListResponse> {
    const endPoint = `${this.endPointB}/programs/native-program-list`;
    return this.httpAuthGet<INativeProgramListResponse>(endPoint);
  }

  public getProgramAccountAddressById(
    addressId: string,
    business?: string,
    program?: string,
    addressType?: string
  ) {
    const endPoint =
      business && program
        ? `${environment.baseApiUrls}/business/${business}/program/${program}/address/${addressId}?addressType=${addressType}`
        : `${this.endPointBP}/address/${addressId}?addressType=${addressType}`;
    return this.httpAuthGet<IBaseAddressListResponse>(endPoint);
  }

  public getProgramAddresses(
    business?: string,
    program?: string
  ): Observable<IBaseAddressListResponse> {
    const endPoint =
      business && program
        ? `${environment.baseApiUrls}/business/${business}/program/${program}/address?fields=DEFAULT`
        : `${this.endPointBP}/address?fields=DEFAULT`;
    return this.httpAuthGet<IBaseAddressListResponse>(endPoint, null, LoaderSpinnerTypes.AgGrid);
  }

  public getProgramBalancenById(
    business?: string,
    program?: string
  ): Observable<IProgramProfileResponse> {
    const endPoint =
      business && program
        ? `${environment.baseApiUrls}/business/${business}/program/${program}/program-account-balance?fields=DEFAULT`
        : `${this.endPointBP}/program-account-balance?fields=DEFAULT`;
    return this.httpAuthGet<IProgramProfileResponse>(endPoint);
  }

  public updateProgramAccountAddress(
    addressId: string,
    addressIdByExternalSystem: string,
    address: IBaseAddress,
    booksRecordsAtBusiness: boolean,
    recordsInCanada: boolean,
    socialMedia: IBaseSocialMedia,
    business?: string,
    program?: string
  ) {
    const payload = {
      addressId,
      addressIdByExternalSystem,
      address: address ? mapBaseAddressToSubmitRequest(address) : null,
      booksRecordsAtBusiness,
      booksRecordsInCanada: recordsInCanada,
      socialMedia: socialMedia
        ? mapBaseSocialMediaToSubmitRequest(socialMedia)
        : null,
    };
    const endPoint =
      business && program
        ? `${environment.baseApiUrls}/business/${business}/program/${program}/address`
        : `${this.endPointBP}/address`;
    return this.httpAuthPut<IBaseResponse>(endPoint, payload);
  }

  public addProgramAccountAddress(
    address: IBaseAddress,
    booksRecordsAtBusiness: boolean,
    recordsInCanada: boolean,
    socialMedia: IBaseSocialMedia,
    business?: string,
    program?: string
  ) {
    const payload = {
      addressId: null,
      addressIdByExternalSystem: null,
      address: address ? mapBaseAddressToSubmitRequest(address) : null,
      booksRecordsAtBusiness,
      booksRecordsInCanada: recordsInCanada,
      socialMedia: socialMedia
        ? mapBaseSocialMediaToSubmitRequest(socialMedia)
        : null,
    };
    const endPoint =
      business && program
        ? `${environment.baseApiUrls}/business/${business}/program/${program}/address`
        : `${this.endPointBP}/address`;
    return this.httpAuthPost<IBaseResponse>(endPoint, payload);
  }

  public deleteProgramAccountAddress(
    addressId: string,
    addressIdByExternalSystem: string,
    address: IBaseAddress,
    booksRecordsAtBusiness: boolean,
    recordsInCanada: boolean,
    relateAddressAndBusiness: string,
    socialMedia: IBaseSocialMedia,
    business?: string,
    program?: string
  ) {
    const payload = {
      addressId,
      addressIdByExternalSystem,
      address: address ? mapBaseAddressToSubmitRequest(address) : null,
      booksRecordsAtBusiness,
      recordsInCanada,
      relateAddressAndBusiness,
      socialMedia: socialMedia
        ? mapBaseSocialMediaToSubmitRequest(socialMedia)
        : null,
    };
    const endPoint =
      business && program
        ? `${environment.baseApiUrls}/business/${business}/program/${program}/address`
        : `${this.endPointBP}/address`;
    return this.httpAuthDelete<IProgramAddressResponse>(
      endPoint,
      null,
      payload
    );
  }

  public getCurrentProgramInformation(): Observable<IProgramProfileResponse> {
    return this.getProgramInformation();
  }

  public getProgramInformation(
    business?: string,
    program?: string
  ): Observable<IProgramProfileResponse> {
    const endPoint =
      business && program
        ? `${environment.baseApiUrls}/business/${business}/program/${program}/info?fields=DEFAULT`
        : `${this.endPointBP}/info?fields=DEFAULT`;
    return this.httpAuthGet<IProgramProfileResponse>(endPoint);
  }

  public getInactiveProgramList(
    business?: string,
  ): Observable<string[]> {
    const endPoint =
        `${environment.baseApiUrls}/business/${business}/programs/inactive-list`
    return this.httpAuthGet<IInactiveProgramResponse>(endPoint).pipe(map(data=>data.programs.map(p=> p.uid)));
  }


  public getProgramOwners(
    business?: string,
    program?: string
  ): Observable<IBusinessContactsResponse> {
    const endPoint =
      business && program
        ? `${environment.baseApiUrls}/business/${business}/program/${program}/owner?fields=DEFAULT`
        : `${this.endPointBP}/owner?fields=DEFAULT`;
    return this.httpAuthGet<IBusinessContactsResponse>(endPoint);
  }

  public updateProgramInformation(
    programInformation: IProgramInformation,
    business?: string,
    program?: string
  ): Observable<IBaseResponse> {
    const endPoint =
      business && program
        ? `${environment.baseApiUrls}/business/${business}/program/${program}/info`
        : `${this.endPointBP}/info`;

    return this.httpAuthPut<IBaseResponse>(
      endPoint,
      mapToProgramInformationSubmitRequest(programInformation)
    );
  }

  public updateBondedCarrierInformation(
    programInformation: IProgramInformation,
    business?: string,
    program?: string
  ): Observable<IBaseResponse> {
    const endPoint =
      business && program
        ? `${environment.baseApiUrls}/business/${business}/program/${program}/bonded-carrier-info`
        : `${this.endPointBP}/bonded-carrier-info`;

    return this.httpAuthPut<IBaseResponse>(
      endPoint,
      mapToProgramInformationSubmitRequest(programInformation)
    );
  }

  public getBusinessInformation(): Observable<IBusinessProfileResponse> {
    const endPoint = `${this.endPointB}/info?fields=DEFAULT`;
    return this.httpAuthGet<IBusinessProfileResponse>(endPoint);
  }

  public updateBusinessInformation(
    info: IBusinessProfileInformationContact
  ): Observable<IBusinessProfileContactInfoResponse> {
    const endPoint = `${this.endPointB}/info?fields=DEFAULT`;
    return this.httpAuthPut<IBusinessProfileContactInfoResponse>(
      endPoint,
      mapToProfileInformationContactToSubmit(info)
    );
  }

  public getActiveSubPrograms(
    loaderSpinnerType: LoaderSpinnerTypes,
    business?: string,
    program?: string
  ): Observable<ISubProgramActiveResponse> {
    const endPoint =
      business && program
        ? `${environment.baseApiUrls}/business/${business}/program/${program}/sub-program?fields=DEFAULT`
        : `${this.endPointBP}/sub-program?fields=DEFAULT`;
    return this.httpAuthGet<ISubProgramActiveResponse>(endPoint, null, loaderSpinnerType);
  }

  public getPendingSubPrograms(
    loaderSpinnerType: LoaderSpinnerTypes,
    business?: string,
    program?: string
  ): Observable<IProgramEnrolmentStatusResponse> {
    const endPoint =
      business && program
        ? `${environment.baseApiUrls}/business/${business}/program/${program}/program/enrolment/drafts`
        : `${this.endPointBP}/program/enrolment/drafts`;
    return this.httpAuthGet<IProgramEnrolmentStatusResponse>(endPoint, null, loaderSpinnerType).pipe(
      map(this.excludeProgramsLists)
    );
  }

  public getBusinessAddresses(loaderSpinnerType: LoaderSpinnerTypes): Observable<IBaseAddressListResponse> {
    const endPoint = `${this.endPointB}/address?fields=DEFAULT`;
    return this.httpAuthGet<IBaseAddressListResponse>(endPoint, null, loaderSpinnerType);
  }

  public getBusinessAddress(
    addressId: string,
    addressType: string
  ): Observable<IBaseAddressResponse> {
    const endPoint = `${this.endPointB}/address/${addressId}?addressType=${addressType}`;
    return this.httpAuthGet<IBaseAddressResponse>(endPoint);
  }

  public updateBusinessAddress(
    address: IBaseAddressSubmitModel
  ): Observable<IBaseResponse> {
    const endPoint = `${this.endPointB}/address`;
    return this.httpAuthPut<IBaseResponse>(endPoint, address);
  }

  public getBusinessContacts(loaderSpinnerType: LoaderSpinnerTypes): Observable<IBaseContactsResponse> {
    const endPoint = `${this.endPointB}/contact?fields=DEFAULT`;
    return this.httpAuthGet<IBaseContactsResponse>(endPoint, null, loaderSpinnerType);
  }

  public getBusinessContact(
    contactId: string
  ): Observable<IBaseContactResponse> {
    const endPoint = `${this.endPointB}/contact/${contactId}`;
    return this.httpAuthGet<IBaseContactResponse>(endPoint);
  }

  public createBusinessContact(contact: IContact): Observable<IBaseResponse> {
    const endPoint = `${this.endPointB}/contact`;
    return this.httpAuthPost<IBaseResponse>(
      endPoint,
      mapToBaseContactSubmit(contact)
    );
  }

  public updateBusinessContact(contact: IContact): Observable<IBaseResponse> {
    const endPoint = `${this.endPointB}/contact`;
    return this.httpAuthPut<IBaseResponse>(
      endPoint,
      mapToBaseContactSubmit(contact)
    );
  }

  public deleteBusinessContact(contact: IContact): Observable<IBaseResponse> {
    const endPoint = `${this.endPointB}/contact`;
    return this.httpAuthDelete<IBaseResponse>(
      endPoint,
      null,
      mapToBaseContactSubmit(contact)
    );
  }

  public getBusinessOwnership(): Observable<IBusinessOwnershipResponse> {
    const endPoint = `${this.endPointB}/owner?fields=DEFAULT`;
    return this.httpAuthGet<IBusinessOwnershipResponse>(endPoint);
  }

  public getBusinessOfficials(): Observable<IBaseContactsResponse> {
    const endPoint = `${this.endPointB}/owner/official`;
    return this.httpAuthGet<IBaseContactsResponse>(endPoint, null, LoaderSpinnerTypes.AgGrid);
  }

  public getBusinessOfficial(
    officialId: string
  ): Observable<IBusinessContactResponse> {
    const endPoint = `${this.endPointB}/owner/official/${officialId}`;
    return this.httpAuthGet<IBusinessContactResponse>(endPoint);
  }

  public createBusinessOfficial(contact: IContact): Observable<IBaseResponse> {
    const endPoint = `${this.endPointB}/owner/official`;
    return this.httpAuthPost<IBaseResponse>(
      endPoint,
      mapContactToSubmitRequest(contact)
    );
  }

  public updateBusinessOfficial(contact: IContact): Observable<IBaseResponse> {
    const endPoint = `${this.endPointB}/owner/official`;
    return this.httpAuthPut<IBaseResponse>(
      endPoint,
      mapContactToSubmitRequest(contact)
    );
  }

  public deleteBusinessOfficial(contact: IContact): Observable<IBaseResponse> {
    const endPoint = `${this.endPointB}/owner/official`;
    return this.httpAuthDelete<IBaseResponse>(
      endPoint,
      null,
      mapContactToSubmitRequest(contact)
    );
  }

  public getBusinessSettingsAndPreferences(): Observable<IBusinessSettingsAndPreferencesResponse> {
    const endPoint = `${this.endPointB}/setting?fields=DEFAULT`;
    return this.httpAuthGet<IBusinessSettingsAndPreferencesResponse>(endPoint);
  }

  public updateBusinessSettings(
    settings: IBusinessSettingsAndPreferences
  ): Observable<IBaseResponse> {
    const endPoint = `${this.endPointB}/setting`;
    return this.httpAuthPut<IBaseResponse>(
      endPoint,
      mapToBusinessSettingsAndPreferencesSubmit(settings)
    );
  }

  public getContacts(
    business?: string,
    program?: string
  ): Observable<IBaseContactsResponse> {
    const endPoint =
      business && program
        ? `${environment.baseApiUrls}/business/${business}/program/${program}/contact`
        : `${this.endPointBP}/contact`;
    return this.httpAuthGet<IBaseContactsResponse>(endPoint, null, LoaderSpinnerTypes.AgGrid);
  }

  public getEdiContacts(
    business?: string,
    program?: string
  ): Observable<IBaseContactsResponse> {
    const endPoint =
      business && program
        ? `${environment.baseApiUrls}/business/${business}/program/${program}/sub-program/edi-contact`
        : `${this.endPointBP}/sub-program/edi-contact`;
    return this.httpAuthGet<IBaseContactsResponse>(endPoint);
  }

  public getContact(
    contactId: string,
    business?: string,
    program?: string
  ): Observable<IBaseContactResponse> {
    const endPoint =
      business && program
        ? `${environment.baseApiUrls}/business/${business}/program/${program}/contact/${contactId}`
        : `${this.endPointBP}/contact/${contactId}`;
    return this.httpAuthGet<IBaseContactResponse>(endPoint);
  }

  public createContact(
    contact: IContact,
    business?: string,
    program?: string
  ): Observable<IBaseResponse> {
    const endPoint =
      business && program
        ? `${environment.baseApiUrls}/business/${business}/program/${program}/contact`
        : `${this.endPointBP}/contact`;

    return this.httpAuthPost<IBaseResponse>(
      endPoint,
      mapToBaseContactSubmit(contact)
    );
  }

  public updateContact(
    contact: IContact,
    business?: string,
    program?: string
  ): Observable<IBaseResponse> {
    const endPoint =
      business && program
        ? `${environment.baseApiUrls}/business/${business}/program/${program}/contact`
        : `${this.endPointBP}/contact`;
    return this.httpAuthPut<IBaseResponse>(
      endPoint,
      mapToBaseContactSubmit(contact)
    );
  }

  public deleteContact(
    contact: IContact,
    business?: string,
    program?: string
  ): Observable<IBaseResponse> {
    const endPoint =
      business && program
        ? `${environment.baseApiUrls}/business/${business}/program/${program}/contact`
        : `${this.endPointBP}/contact`;
    return this.httpAuthDelete<IBaseResponse>(
      endPoint,
      null,
      mapToBaseContactSubmit(contact)
    );
  }

  public getProgramEdiServices(
    loaderSpinnerType: LoaderSpinnerTypes,
    business?: string,
    program?: string
  ): Observable<IProgramEdiServiceListResponse> {
    const endPoint =
      business && program
        ? `${environment.baseApiUrls}/business/${business}/program/${program}/edi`
        : `${this.endPointBP}/edi`;
    return this.httpAuthGet<IProgramEdiServiceListResponse>(endPoint, null, loaderSpinnerType);
  }

  public getWarehouseProgram(
    programId: string,
    business?: string
  ): Observable<IProgramProfileResponse> {
    const endPoint = business
      ? `${environment.baseApiUrls}/business/${business}/program/${programId}/info`
      : `${this.endPointB}/program/${programId}/info`;

    return this.httpAuthGet<IProgramProfileResponse>(endPoint);
  }

  private excludeProgramsLists(
    response: IProgramEnrolmentStatusResponse
  ): IProgramEnrolmentStatusResponse {
    return {
      ...response,
      enrolments: response?.enrolments?.filter(
        c => c?.programType === ProgramEnrolmentType.SubProgram
      ),
    };
  }
}
